import { P2GBox, P2GLinkGroup } from '@parcel2go/core-ui';
import { P2GDropdown } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GProgress } from '@parcel2go/core-ui';
import { P2GTextField } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useCallback, useMemo, useState } from 'react';
import { Country, Customer, PackageApiModel, ProductApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SumNumber } from '../../../lib/helpers/ArrayHelper';
import AlertWithIcon from '../../AlertWithIcon';
import { CustomerSelector } from '../../customer/CustomerSelector';
import { ProductSelector } from '../../product/ProductSelector';
import { DimensionsSelection } from '../DimensionsSelection';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { PanelButtons } from './PanelButtons';
import { SmartSendCreateFormValidation } from '../../../lib/helpers/SmartSendValidation';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ConfigSetting, useTenant } from '@parcel2go/npm-p2g-app';
import { SettingsCache } from '~/lib/consts/settings-cache';
import AddressTypeahead from '../../address/typeahead';
import useMapAddressLookup from '../../address/MapAddressLookupToCustomer';
import useOnLoad from '~/routes/use-on-load';
import { createNewSmartSendItem } from './createNewSmartSendItem';

interface props {
  getCustomers: () => Promise<Customer[]>;
  getProducts: () => Promise<ProductApiModel[]>;
  getPackages: () => Promise<PackageApiModel[]>;
  getCountries: () => Promise<Country[]>;
  onSave: (item: SmartSendItemApiModel) => void;
  close?: () => void;
  sender: Customer;
  recipient?: Customer;
  hideDimensionsIfNotRequired: boolean;
}

const initialise = (sender: Customer, isoCode: string, recipient?: Customer) => {
  const output = {
    ...createNewSmartSendItem(isoCode),
    Sender: { ...sender }
  };
  if (recipient) {
    output.Recipient = { ...output.Recipient, ...recipient };
  }
  return output;
};

export const ItemCreatePanel: React.FC<props> = (props) => {
  const { settings } = useTenant();
  const [item, setItem] = useState<SmartSendItemApiModel>(
    initialise(props.sender, settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY], props.recipient)
  );
  const [countries, setCountries] = useState<Country[]>([]);
  const [saving, setSaving] = useState(false);
  const [productId, setProductId] = useState('');
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { mapAddressLookupToCustomer } = useMapAddressLookup();
  const [showSearch, setShowSearch] = useState(true);

  const country = useMemo(
    () =>
      countries.find(
        (x) =>
          x.CountryIsoCode === item.Recipient.CountryIsoCode &&
          x.CountrySubdivision === item.Recipient.CountrySubdivision
      ),
    [countries, item.Recipient.CountryIsoCode, item.Recipient.CountrySubdivision]
  );

  const formIsInvalid = useMemo(() => SmartSendCreateFormValidation(item).length > 0, [item]);

  useOnLoad(() => {
    const load = async () => {
      const r = await props.getCountries();
      setCountries(r);
    };
    load();
  });

  const changeCountry = useCallback(
    (iso3Code: string, subdivision: string, i: SmartSendItemApiModel) => {
      const switchCountry = countries.find(
        (c) => c.CountryIsoCode == iso3Code && c.CountrySubdivision == (subdivision === 'null' ? null : subdivision)
      );
      if (switchCountry) {
        i.Recipient.CountryIsoCode = switchCountry.CountryIsoCode;
        i.Recipient.CountrySubdivision = switchCountry.CountrySubdivision;
        if (switchCountry.States?.length > 0 && !switchCountry.States.find((s) => s.Code === i.Recipient.County)) {
          i.Recipient.County = switchCountry.States[0].Code;
        }
      }
    },
    [countries]
  );

  const changeBasedOnPostcode = useCallback(
    (customer: Customer, item: SmartSendItemApiModel) => {
      const possibleSwitch = country?.Switches?.find((s) =>
        new RegExp(s.Pattern).test(customer?.Postcode?.toUpperCase())
      );
      if (possibleSwitch) {
        changeCountry(possibleSwitch.ToIsoCode, possibleSwitch.ToSubdivision, item);
      }
    },
    [country, changeCountry]
  );

  const setRecipientDetails = useCallback(
    (field: string, v: string) => {
      const i = { ...item };
      i.Recipient[field] = v === 'null' ? null : v;

      // Check this postcode isn't for a different country....
      if (field === 'Postcode') {
        changeBasedOnPostcode(i.Recipient, i);
      }

      setItem(i);
    },
    [item, changeBasedOnPostcode]
  );

  if (saving) {
    return (
      <AlertWithIcon icon={<P2GProgress />}>
        <P2GText>{t('CREATING_NEW_ITEM_ALERT', { defaultValue: 'Creating new item...' })}</P2GText>
      </AlertWithIcon>
    );
  }

  return (
    <>
      <SidePanelSections>
        <SidePanelSection title={t('SENDER_SUBTITLE', { defaultValue: 'Sender:' })}>
          <P2GBox>
            <P2GText variant="h6">{item.Sender.Name}</P2GText>
            <br />
            <P2GText>
              {item.Sender.AddressLine1} {item.Sender.AddressLine2}, {item.Sender.Postcode}
            </P2GText>
          </P2GBox>
        </SidePanelSection>

        <SidePanelSection title={t('RECIPIENT_SUBTITLE', { defaultValue: 'Recipient:' })}>
          <P2GGrid container spacing={2}>
            <P2GGrid item xs={12}>
              <P2GTextField
                fullWidth
                maxLength={50}
                value={item.Recipient.Name}
                required
                onChange={(v) => setRecipientDetails('Name', v.currentTarget.value)}
                label={t('NAME_LABEL', { defaultValue: 'Name' })}
              />
            </P2GGrid>
            <P2GGrid item xs={12}>
              <P2GTextField
                fullWidth
                maxLength={50}
                value={item.Recipient.Organisation}
                onChange={(v) => setRecipientDetails('Organisation', v.currentTarget.value)}
                label={t('ORGANISATION_LABEL', { defaultValue: 'Organisation' })}
              />
            </P2GGrid>
            <P2GGrid item xs={12}>
              <P2GLinkGroup
                links={[
                  <CustomerSelector
                    key="customer-selector"
                    customers={props.getCustomers}
                    onSelect={(c) => {
                      const updated = { ...item, Recipient: c };
                      changeBasedOnPostcode(c, updated);
                      setItem(updated);
                    }}
                    text={t('BROWSE_ADDRESSES_LINK', { defaultValue: 'Browse Saved Addresses' })}
                  />,
                  !showSearch && (
                    <P2GLink onClick={() => setShowSearch(true)} key="search-address-link">
                      {t('SEARCH_FOR_ADDRESS_LINK', { defaultValue: 'Search for another address' })}
                    </P2GLink>
                  )
                ]}
              />
            </P2GGrid>

            <P2GGrid item xs={12}>
              {showSearch && (
                <AddressTypeahead
                  required={false}
                  onSelect={async (c) => {
                    const recipient = await mapAddressLookupToCustomer(
                      item.Recipient,
                      c,
                      'Recipient',
                      item.Recipient.AccountName || 'Default'
                    );
                    const updated = { ...item, Recipient: recipient };
                    setItem(updated);
                    setShowSearch(false);
                  }}
                  countryIsoCode={item.Recipient.CountryIsoCode}
                  search={''}
                />
              )}
            </P2GGrid>
            <P2GGrid item xs={6}>
              <P2GTextField
                fullWidth
                maxLength={50}
                value={item.Recipient.AddressLine1}
                required
                onChange={(v) => setRecipientDetails('AddressLine1', v.currentTarget.value)}
                label={t('HOUSE_NUMBER_LABEL', { defaultValue: 'House Number' })}
                id="AddressLine1"
              />
            </P2GGrid>
            <P2GGrid item xs={6}>
              <P2GTextField
                fullWidth
                maxLength={50}
                value={item.Recipient.AddressLine2}
                required
                onChange={(v) => setRecipientDetails('AddressLine2', v.currentTarget.value)}
                label={t('STREET_LABEL', { defaultValue: 'Street' })}
                id="AddressLine2"
              />
            </P2GGrid>
            <P2GGrid item xs={6}>
              <P2GTextField
                fullWidth
                maxLength={50}
                value={item.Recipient.Town}
                required
                onChange={(v) => setRecipientDetails('Town', v.currentTarget.value)}
                label={t('TOWN_LABEL', { defaultValue: 'Town' })}
              />
            </P2GGrid>
            {country && (
              <P2GGrid item xs={6} key={`county_${country.CountryIsoCode}_${country.States?.length}`}>
                {country?.States?.length > 0 ? (
                  <P2GDropdown
                    fullWidth
                    required={false}
                    options={
                      country?.States.map((x) => ({ value: x.Code, text: x.Name, key: `${x.Code}_${x.Name}` })) || []
                    }
                    value={item.Recipient.County}
                    label={t('COUNTY_LABEL', { defaultValue: 'County' })}
                    key={'County'}
                    id={'County'}
                    onChange={(v: string) => setRecipientDetails('County', v)}
                  />
                ) : (
                  <P2GTextField
                    fullWidth
                    showErrorsWhenPristine={true}
                    value={item.Recipient.County}
                    label={t('COUNTY_LABEL', { defaultValue: 'County' })}
                    key={'County'}
                    id={'County'}
                    onChange={(v) => setRecipientDetails('County', v.currentTarget.value)}
                  />
                )}
              </P2GGrid>
            )}
            {countries.length > 0 && (
              <P2GGrid item xs={6}>
                <P2GDropdown
                  fullWidth
                  id="Country"
                  label={t('COUNTRY_LABEL', { defaultValue: 'Country' })}
                  value={`${item.Recipient.CountryIsoCode}|${item.Recipient.CountrySubdivision}`}
                  options={countries.map((p) => ({
                    value: `${p.CountryIsoCode}|${p.CountrySubdivision}`,
                    text: p.LocalisedName,
                    key: `${p.CountryIsoCode}|${p.CountrySubdivision}|${p.Name}`
                  }))}
                  onChange={(v: string) => {
                    const i = { ...item };
                    changeCountry(v.split('|')[0], v.split('|')[1], i);
                    setItem(i);
                  }}
                />
              </P2GGrid>
            )}
            <P2GGrid item xs={6}>
              <P2GTextField
                fullWidth
                maxLength={50}
                value={item.Recipient.Postcode}
                required
                onChange={(v) => setRecipientDetails('Postcode', v.currentTarget.value)}
                label={t('POSTCODE_LABEL', { defaultValue: 'Postcode' })}
              />
            </P2GGrid>
            <P2GGrid item xs={6}>
              <P2GTextField
                fullWidth
                maxLength={50}
                type="tel"
                value={item.Recipient.Phone}
                onChange={(v) => setRecipientDetails('Phone', v.currentTarget.value)}
                label={t('PHONE_LABEL', { defaultValue: 'Phone' })}
              />
            </P2GGrid>
            <P2GGrid item xs={12}>
              <P2GTextField
                fullWidth
                type="email"
                maxLength={256}
                value={item.Recipient.Email}
                onChange={(v) => setRecipientDetails('Email', v.currentTarget.value)}
                label={t('EMAIL_LABEL', { defaultValue: 'Email' })}
              />
            </P2GGrid>
          </P2GGrid>
        </SidePanelSection>

        <SidePanelSection title={t('PARCEL_DETAILS_SUBTITLE', { defaultValue: 'Parcel Details:' })}>
          <P2GGrid container spacing={2}>
            <P2GGrid item xs={12}>
              <P2GTextField
                fullWidth
                value={item.Description}
                required
                type="text"
                maxLength={256}
                onChange={(v) => {
                  const i = { ...item, Description: v.currentTarget.value };
                  i.MarketplaceOrderLines.forEach((p) => {
                    p.Product.Description = v.currentTarget.value;
                  });
                  setItem(i);
                }}
                label={t('DESCRIPTION_LABEL', { defaultValue: 'Description' })}
              />
            </P2GGrid>
            {SettingsCache.settings.Features.includes('Products') && (
              <P2GGrid item xs={12}>
                <ProductSelector
                  products={props.getProducts}
                  onSelect={(p) => {
                    const i = { ...item };
                    i.Description = p.Description;
                    i.PackageDimensions = p.Dimensions;
                    if (p.TariffCode) {
                      i.MarketplaceOrderLines[0].Product.TariffCode = p.TariffCode;
                    }
                    if (p.Value) {
                      i.MarketplaceOrderLines[0].Value = p.Value;
                      i.Value = SumNumber(i.MarketplaceOrderLines.map((x) => x.Value));
                    }
                    setItem(i);
                    setProductId(p.Id);
                  }}
                >
                  <P2GLink>{t('BROWSE_PRODUCTS_LINK', { defaultValue: 'Browse Products / Inventory' })}</P2GLink>
                </ProductSelector>
              </P2GGrid>
            )}
            <P2GGrid item xs={6}>
              <P2GTextField
                fullWidth
                value={item.MarketplaceOrderLines[0].Value as any}
                required
                max={5000}
                min={0.01}
                step={0.01}
                type="number"
                onChange={(v) => {
                  const i = { ...item };
                  i.MarketplaceOrderLines[0].Value = Number(v.currentTarget.value);
                  i.Value = SumNumber(i.MarketplaceOrderLines.map((x) => x.Value));
                  setItem(i);
                }}
                label={t('VALUE_LABEL', { defaultValue: 'Value' })}
              />
            </P2GGrid>
            <P2GGrid item xs={6}>
              <P2GTextField
                fullWidth
                maxLength={50}
                type="text"
                value={item.MarketplaceOrderLines[0].Product.Sku}
                onChange={(v) => {
                  const i = { ...item };
                  i.MarketplaceOrderLines[0].Product.Sku = v.currentTarget.value;
                  setItem(i);
                }}
                label={t('SKU_LABEL', { defaultValue: 'SKU' })}
              />
            </P2GGrid>
          </P2GGrid>
        </SidePanelSection>

        <SidePanelSection title={t('PARCEL_SIZE_SUBTITLE', { defaultValue: 'Parcel Size:' })} key={productId}>
          <DimensionsSelection
            startMinimised={false}
            dimensions={item.PackageDimensions}
            onUpdate={(d) => setItem({ ...item, PackageDimensions: d })}
            dimensionsRequired={!props.hideDimensionsIfNotRequired}
            hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
            getPackages={props.getPackages}
          />
        </SidePanelSection>

        <SidePanelSection title={t('ORDER_DETAILS_SUBTITLE', { defaultValue: 'Order Details:' })}>
          <P2GGrid container spacing={2}>
            <P2GGrid item xs={12}>
              <P2GTextField
                fullWidth
                type="text"
                maxLength={50}
                value={item.MarketplaceOrderLines[0].MarketplaceOrderId}
                onChange={(v) => {
                  const i = { ...item };
                  i.MarketplaceOrderLines[0].MarketplaceOrderId = v.currentTarget.value;
                  setItem(i);
                }}
                label={t('REFERENCE_NUMBER_LABEL', { defaultValue: 'Reference Number' })}
              />
            </P2GGrid>
          </P2GGrid>
        </SidePanelSection>
      </SidePanelSections>
      <P2GBox mt={2} />
      <PanelButtons
        onSave={() => {
          setSaving(true);
          props.onSave(item);
        }}
        saveDisabled={formIsInvalid}
        onCancel={props.close}
      />
    </>
  );
};
